import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { LobbyZeile, Me } from '../lib/types';

/**
 * Was man nach dem Anmelden sieht, solange keine bestimmte Lobby angesteuert wurde.
 *
 * Wer genau eine hat, wird durchgereicht – das ist der Normalfall und soll keinen Klick kosten.
 * Wer mehrere hat, wählt. Und Saem kann hier eine neue anlegen, denn vorher gibt es gar nichts,
 * worin sie arbeiten könnte.
 */
export function Lobbywahl({
  me,
  onWahl,
  onLogout,
}: {
  me: Me;
  onWahl: (lobbyId: string) => void;
  onLogout: () => void;
}) {
  const [lobbys, setLobbys] = useState<LobbyZeile[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);

  useEffect(() => {
    void api
      .lobbies()
      .then((liste) => {
        setLobbys(liste);
        /*
         * Genau eine Lobby: direkt hinein, ohne Zwischenseite.
         *
         * Nicht für den Super-Admin: Er sieht **alle** Lobbys, und in eine fremde hineingeworfen
         * zu werden, ist nicht das, was er beim Anmelden will. Für ihn ist die Übersicht das Ziel.
         */
        if (liste.length === 1 && me.role !== 'SUPER_ADMIN') onWahl(liste[0]!.id);
      })
      .catch((err) => {
        setLobbys([]);
        setFehler(err instanceof ApiError ? err.message : 'Die Übersicht ließ sich nicht laden.');
      });
  }, [onWahl]);

  if (!lobbys) {
    return (
      <main className="page">
        <p className="muted">einen Moment …</p>
      </main>
    );
  }

  /*
   * Wer was anlegen darf: Saem produktive Lobbys, Test-Admin und Super-Admin Testlobbys.
   *
   * Das ist keine Feinheit der Oberfläche – an eine Testlobby kommt Saem anschließend selbst
   * nicht heran, weil Test- und Produktivbetrieb getrennt sind.
   */
  const darfAnlegen = me.role === 'LOBBY_OWNER' || me.role === 'TEST_ADMIN' || me.role === 'SUPER_ADMIN';
  const nurTest = me.role !== 'LOBBY_OWNER';

  return (
    <main className="page narrow">
      <header className="page-head">
        <h1>Win-Challenge</h1>
        <button type="button" className="ghost" onClick={onLogout}>
          Abmelden
        </button>
      </header>

      {fehler && <p className="notice">{fehler}</p>}

      {me.role === 'SUPER_ADMIN' && (
        <button type="button" onClick={() => window.location.assign('/verwaltung')}>
          Zur Verwaltung
        </button>
      )}

      {lobbys.length > 0 && (
        <ul className="zeilen">
          {lobbys.map((lobby) => (
            <li key={lobby.id} className="zeile">
              <button type="button" className="zeile-text link" onClick={() => onWahl(lobby.id)}>
                {lobby.name}
              </button>
              <span className="muted small">
                {lobby.boardSize}×{lobby.boardSize}
                {lobby.isTest ? ' · Test' : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      {lobbys.length === 0 && !neu && (
        <p className="muted">
          {!darfAnlegen
            ? 'Du gehörst noch zu keiner Lobby. Sag Saem Bescheid, dass sie dich einträgt.'
            : nurTest
              ? 'Noch keine Testlobby. Leg eine an, dann kannst du proben.'
              : 'Noch keine Lobby. Leg eine an, dann kann es losgehen.'}
        </p>
      )}

      {darfAnlegen &&
        (neu ? (
          <NeueLobby nurTest={nurTest} onFertig={onWahl} onAbbruch={() => setNeu(false)} />
        ) : (
          <button type="button" className="primary" onClick={() => setNeu(true)}>
            {nurTest ? 'Neue Testlobby anlegen' : 'Neue Lobby anlegen'}
          </button>
        ))}
    </main>
  );
}

/**
 * Nur das Nötigste.
 *
 * Alles Weitere – Teams, Aufgaben, Mitspieler – steht danach in der Einrichtung. Ein Formular, das
 * vorab zwanzig Fragen stellt, hält nur auf; die Lobby entsteht mit brauchbaren Vorgaben und wird
 * dann in Ruhe eingerichtet.
 */
function NeueLobby({
  nurTest,
  onFertig,
  onAbbruch,
}: {
  nurTest: boolean;
  onFertig: (lobbyId: string) => void;
  onAbbruch: () => void;
}) {
  const [name, setName] = useState('');
  const [größe, setGröße] = useState(5);
  const [modus, setModus] = useState<'SHARED' | 'INDIVIDUAL'>('SHARED');
  const [minuten, setMinuten] = useState(90);
  const [fehler, setFehler] = useState<string | null>(null);
  const [läuft, setLäuft] = useState(false);

  async function anlegen(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null);
    setLäuft(true);
    try {
      const lobby = await api.createLobby({
        name,
        boardSize: größe,
        boardMode: modus,
        taskOrder: 'RANDOM',
        durationMinutes: minuten,
        ...(nurTest ? { isTest: true } : {}),
      });
      onFertig(lobby.id);
    } catch (err) {
      setFehler(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
      setLäuft(false);
    }
  }

  return (
    <form className="stack panel" onSubmit={(e) => void anlegen(e)}>
      <h2>{nurTest ? 'Neue Testlobby' : 'Neue Lobby'}</h2>
      {nurTest && (
        <p className="muted small">
          Eine Testlobby ist vom echten Betrieb getrennt: Darin lässt sich proben, und niemand
          außerhalb bekommt davon etwas mit.
        </p>
      )}
      <label>
        Name des Abends
        <input
          value={name}
          maxLength={80}
          autoFocus
          placeholder="z. B. Freitagsrunde"
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <div className="feld-paar">
        <label>
          Boardgröße
          <select value={größe} onChange={(e) => setGröße(Number(e.target.value))}>
            {[3, 4, 5, 6, 7, 8, 9].map((n) => (
              <option key={n} value={n}>
                {n} × {n}
              </option>
            ))}
          </select>
        </label>
        <label>
          Spielzeit in Minuten
          <input
            type="number"
            min={1}
            max={1440}
            value={minuten}
            onChange={(e) => setMinuten(Number(e.target.value))}
          />
        </label>
      </div>

      <label>
        Board
        <select value={modus} onChange={(e) => setModus(e.target.value as typeof modus)}>
          <option value="SHARED">Ein Board für alle</option>
          <option value="INDIVIDUAL">Ein eigenes Board je Team</option>
        </select>
      </label>

      {fehler && <p className="notice">{fehler}</p>}

      <div className="row">
        <button type="submit" className="primary" disabled={läuft || !name.trim()}>
          Anlegen
        </button>
        <button type="button" className="ghost" onClick={onAbbruch} disabled={läuft}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
